import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTitle } from "../useTitle";
import { Board, Loading, Notice, PageHead } from "../components/Look";
import { ApiError, api, ago, type QualityGroup } from "../api";
import { MarkdownView } from "../components/Markdown";
import i18n from "../i18n";

/** The letter is the grade the reviewer wrote and never changes; the gloss is
 *  the sentence beside it, and that is the part a reader has to understand. */
function gloss(grade: string): string {
  const letter = (grade || "?")[0];
  if (!"ABCDE".includes(letter)) return "";
  return i18n.t(`quality.gloss.${letter}` as "quality.gloss.A");
}

function Chip({ on, onClick, children }: {
  on: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={on}
      className={`rounded-full border px-3 py-1 text-xs ${
        on
          ? "border-brand-700 bg-brand-700 text-white dark:border-sky-300 dark:bg-sky-300 dark:text-slate-900"
          : "border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function gradeTone(g: string): string {
  switch ((g || "?")[0]) {
    case "A":
    case "B":
      return "bg-success-100 text-success-900 dark:bg-success-950 dark:text-success-200";
    case "C":
      return "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200";
    default:
      return "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200";
  }
}

/**
 * What the suite actually proves, group by group.
 *
 * The grade is JUDGEMENT and comes from the bench's quality.yaml, written by
 * hand. The counts beside it are computed by the harness from the plan and the
 * scenarios every time, never stored - a number written down is believed long
 * after it stopped being true.
 *
 * The evidence column is answered HERE, from every run this service has been
 * sent, rather than from whatever logs happen to be on one bench's disk.
 *
 * Staleness is shown, not hidden: a stale grade did not become wrong, it means
 * nobody has looked since the scenarios moved. Presenting it as current is the
 * failure this page exists to avoid.
 */
export default function Quality() {
  const { t } = useTranslation();
  useTitle(t("quality.title"));
  const [loaded, setLoaded] = useState(false);
  const [rows, setRows] = useState<QualityGroup[]>([]);
  const [bench, setBench] = useState<string | null>(null);
  const [at, setAt] = useState<number | null>(null);
  const [benches, setBenches] = useState<{ id: number; name: string; reviewed: boolean }[]>([]);
  // The bench in the address, so a link can open this page on it and a reload
  // keeps it. Without one, the service picks the bench that reported last.
  const [params, setParams] = useSearchParams();
  const fromUrl = Number(params.get("bench")) || undefined;
  const [pick, setPickState] = useState<number | undefined>(fromUrl);
  const setPick = (id: number) => {
    setPickState(id);
    setParams((p) => {
      p.set("bench", String(id));
      return p;
    }, { replace: true });
  };
  const [error, setError] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [only, setOnly] = useState<"" | "weak" | "stale">("");

  useEffect(() => {
    api
      .quality(pick)
      .then((d) => {
        setRows(d.quality);
        setLoaded(true);
        setBench(d.bench);
        setAt(d.received_at);
        setBenches(d.benches ?? []);
        if (pick === undefined && d.bench_id) setPickState(d.bench_id);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [pick]);

  const stale = rows.filter((r) => r.stale).length;
  // C or worse: A and B are "proves what it claims" and "holds up", and the
  // reason to open this page is the other three.
  const weak = (g: string) => !["A", "B"].includes((g || "?")[0]);
  const shown = rows.filter((r) =>
    only === "weak" ? weak(r.grade) : only === "stale" ? r.stale : true,
  );

  return (
    <section>
      <PageHead
        title={t("quality.heading")}
        answer={
          rows.length > 0
            ? stale > 0
              ? t("quality.staleCount", { count: stale, total: rows.length })
              : t("quality.answerAllFresh", { count: rows.length })
            : undefined
        }
        after={<p className="text-sm text-slate-500">{t("quality.intro")}</p>}
      />
      {!loaded && <Loading label={t("common.loading")} />}

      {error && <Notice tone="error">{error}</Notice>}

      {loaded && rows.length === 0 && !error && (
        <p className="text-base text-slate-500">{t("quality.empty")}</p>
      )}

      {rows.length > 0 && (
        <>
          <p className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            {benches.length > 1 ? (
              <select
                className="rounded border border-slate-300 px-1 py-0.5 text-xs dark:border-slate-700 dark:bg-slate-800"
                value={pick ?? ""}
                onChange={(e) => setPick(Number(e.target.value))}
                aria-label={t("quality.whichBench")}
              >
                {benches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                    {b.reviewed ? "" : t("quality.noReview")}
                  </option>
                ))}
              </select>
            ) : (
              <span>{t("quality.fromBench", { bench })}</span>
            )}
            <span>{t("quality.received", { when: at ? ago(at) : t("common.dash") })}</span>
          </p>

          <div className="mt-3 flex flex-wrap gap-1.5">
            <Chip on={only === "weak"} onClick={() => setOnly(only === "weak" ? "" : "weak")}>
              {t("quality.chipWeak", {
                count: rows.filter((r) => weak(r.grade)).length,
              })}
            </Chip>
            <Chip on={only === "stale"} onClick={() => setOnly(only === "stale" ? "" : "stale")}>
              {t("quality.chipStale", { count: stale })}
            </Chip>
          </div>

          {/* THE GRADE IS THE PAGE, and it was a one-character badge with its
              meaning in a `title`. Thirty-four rows of identical weight, three
              numbers run together on the right with no headers, and the gloss -
              "no proof at all" - never on screen at all. */}
          <div className="mt-3 hidden grid-cols-[13rem_1fr_7rem_9rem_6rem] gap-x-4 px-3 text-xs font-medium text-slate-500 sm:grid">
            <span>{t("quality.colGrade")}</span>
            <span>{t("quality.colGroup")}</span>
            <span className="text-right">{t("quality.colScenario")}</span>
            <span className="text-right">{t("quality.colEverPassed")}</span>
            <span className="text-right">{t("quality.colConfidence")}</span>
          </div>

          <Board className="mt-1">
            {shown.map((r) => {
              const ev = r.evidence;
              const isOpen = open === r.id;
              return (
                <div key={r.id}>
                  <button
                    className="grid w-full grid-cols-1 items-center gap-x-4 gap-y-1 px-4 py-3 text-left text-sm hover:bg-slate-50 sm:grid-cols-[13rem_1fr_7rem_9rem_6rem] dark:hover:bg-slate-800/50"
                    onClick={() => setOpen(isOpen ? null : r.id)}
                    aria-expanded={isOpen}
                  >
                    {/* The letter AND what it means. The gloss existed in this
                        file and only ever reached a tooltip. */}
                    <span className={`inline-flex items-baseline gap-1.5 rounded px-2 py-0.5 text-xs ${gradeTone(r.grade)}`}>
                      <b className="font-mono font-semibold">{r.grade}</b>
                      <span className="leading-tight">{gloss(r.grade)}</span>
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{r.name}</span>
                      {r.stale && (
                        <span
                          title={t("quality.staleTitle")}
                          className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-300"
                        >
                          {t("quality.staleBadge")}
                        </span>
                      )}
                    </span>
                    {/* THE COLUMN HEADERS ARE hidden BELOW sm, so each number
                        carries its own name there. Without this a phone shows
                        three unlabelled numbers in a stack - the very defect
                        the header row above was added to fix on a desktop. */}
                    <span className="text-xs tabular-nums text-slate-500 sm:text-right">
                      <span className="sm:hidden">{t("quality.colScenario")}: </span>
                      {r.scen}/{r.cases}
                    </span>
                    <span
                      className={`text-xs tabular-nums sm:text-right ${
                        ev && ev.unseen > 0
                          ? "text-amber-700 dark:text-amber-400"
                          : "text-slate-500"
                      }`}
                      title={t("quality.everPassedTitle")}
                    >
                      <span className="sm:hidden">{t("quality.colEverPassed")}: </span>
                      {ev
                        ? `${ev.ever_passed}${
                            ev.unseen > 0
                              ? t("quality.neverSuffix", { count: ev.unseen })
                              : ""
                          }`
                        : t("common.dash")}
                    </span>
                    <span className="text-xs text-slate-500 sm:text-right">
                      <span className="sm:hidden">{t("quality.colConfidence")}: </span>
                      {r.confidence || t("common.dash")}
                    </span>
                  </button>

                  {isOpen && (
                    // 70-odd characters, not 180. This is the longest prose in
                    // the app and it was set to the full width of a desktop
                    // window, which is about twice a comfortable line.
                    <div className="max-w-prose border-t border-slate-100 bg-slate-50/60 px-4 py-3 text-sm dark:border-slate-800 dark:bg-slate-950/30">
                      {r.verdict && <MarkdownView md={r.verdict} />}
                      {(r.gaps?.length ?? 0) > 0 && (
                        <>
                          <h3 className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                            {t("quality.gaps")}
                          </h3>
                          <MarkdownView md={r.gaps!.map((g) => `- ${g}`).join("\n")} />
                        </>
                      )}
                      {(r.recommendations?.length ?? 0) > 0 && (
                        <>
                          <h3 className="mt-2 text-sm font-semibold text-slate-700 dark:text-slate-300">
                            {t("quality.recommendations")}
                          </h3>
                          <MarkdownView
                            md={r
                              .recommendations!.map(
                                (x) => `- ${x.cost ? `**[${x.cost}]** ` : ""}${x.text}`,
                              )
                              .join("\n")}
                          />
                        </>
                      )}
                      {ev && ev.never_passed.length > 0 && (
                        <p className="mt-2 text-xs text-slate-500">
                          <b>{t("quality.neverPassed")}</b>{" "}
                          <span className="font-mono">
                            {ev.never_passed.join(", ")}
                          </span>
                        </p>
                      )}
                      <p className="mt-2 text-xs text-slate-400">
                        {t("quality.reviewed", {
                          when: r.reviewed_at ?? t("common.dash"),
                        })}
                        {r.reviewed_by
                          ? t("quality.reviewedBy", { who: r.reviewed_by })
                          : ""}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </Board>
        </>
      )}
    </section>
  );
}
